import { useMutation } from '@tanstack/react-query';
import { API_URL, getAuthHeaders } from '../utils/api';

interface ValidationError {
  field: string;
  code: string;
}

interface PreviewRow {
  row: number;
  data: Record<string, string>;
  isValid: boolean;
  errors: ValidationError[];
  /** Почта уже есть: строка только вписывает человека в отдел или команду. */
  isExisting?: boolean;
}

export interface PreviewResult {
  rows: PreviewRow[];
  summary: { total: number; valid: number; invalid: number };
}

export interface ImportResult {
  imported: number;
  /** Известные люди, вписанные в отдел или команду. */
  updated?: number;
  skipped: number;
  errors: Array<{ row: number; errors: ValidationError[] }>;
}

/** Причина отказа от сервера (403 — нет права на структуру и т. п.), иначе общий текст. */
const failure = async (response: Response, fallback: string) =>
  ((await response.json().catch(() => null)) as { message?: string } | null)?.message || fallback;

export const useImportPreview = () => {
  return useMutation({
    mutationFn: async ({ url, file }: { url: string; file: File }): Promise<PreviewResult> => {
      const formData = new FormData();
      formData.append('file', file);
      const headers: Record<string, string> = {};
      const authHeaders = getAuthHeaders();
      if (authHeaders.Authorization) headers.Authorization = authHeaders.Authorization;
      const response = await fetch(`${API_URL}${url}?preview=true`, {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!response.ok) throw new Error(await failure(response, 'Preview failed'));
      return response.json();
    },
  });
};

export const useImportExecute = () => {
  return useMutation({
    mutationFn: async ({ url, file }: { url: string; file: File }): Promise<ImportResult> => {
      const formData = new FormData();
      formData.append('file', file);
      const headers: Record<string, string> = {};
      const authHeaders = getAuthHeaders();
      if (authHeaders.Authorization) headers.Authorization = authHeaders.Authorization;
      const response = await fetch(`${API_URL}${url}`, {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!response.ok) throw new Error(await failure(response, 'Import failed'));
      return response.json();
    },
  });
};

export async function downloadExport(url: string, format: 'csv' | 'xlsx') {
  const response = await fetch(`${API_URL}${url}?format=${format}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Export failed');
  const blob = await response.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `export.${format}`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export async function downloadTemplate(url: string, format: 'csv' | 'xlsx') {
  const response = await fetch(`${API_URL}${url}?format=${format}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Template download failed');
  const blob = await response.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `template.${format}`;
  a.click();
  URL.revokeObjectURL(a.href);
}
